import { useTheme } from "next-themes"
import { Toaster as Sonner, toast as sonnerToast } from "sonner"
import React, { HTMLAttributes } from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

type ToasterProps = React.ComponentProps<typeof Sonner>

const SonnerToaster = ({ ...props }: ToasterProps) => {
  const { theme = `light` } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast: `group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg`,
          title: `toast-title`,
          icon: `toast-icon`,
          success: `toast-success`,
          error: `toast-error`,
          description: `group-[.toast]:text-secondary-foreground!`,
          actionButton: `group-[.toast]:bg-primary group-[.toast]:text-primary-foreground`,
          cancelButton: `group-[.toast]:bg-muted group-[.toast]:text-muted-foreground`,
        },
      }}
      {...props}
    />
  )
}

const toastVariants = cva(
  `group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full`,
  {
    variants: {
      variant: {
        default: `border bg-background text-foreground`,
        success: `bg-white border text-foreground [&_[data-type="title"]]:text-[#03A300]`,
        destructive: `destructive group border-destructive bg-destructive text-destructive-foreground`,
      },
    },
    defaultVariants: {
      variant: `default`,
    },
  }
)

type ToastVariant = VariantProps<typeof toastVariants>["variant"]

type ToastComponentProps = ToastProps & {
  variant?: ToastVariant
  className?: HTMLAttributes<HTMLDivElement>["className"]
}

const customToast = (t: Partial<Omit<ToastComponentProps, "id">>) =>
  sonnerToast.custom((id) => (
    <Toast
      id={id}
      title={t.title}
      description={t.description}
      button={
        t.button && {
          label: t.button.label,
          onClick: t.button.onClick,
        }
      }
      variant={t.variant}
      className={t.className}
    />
  ))

export const Toast = (props: Partial<ToastComponentProps>) => {
  const { title, description, button, id, variant } = props

  return (
    <div className={cn(toastVariants({ variant }), props.className)}>
      <div className="flex flex-1 items-center">
        <div className="w-full">
          <p className="text-sm font-medium" data-type="title">
            {title}
          </p>
          {description && <p className="mt-1 text-sm">{description}</p>}
        </div>
      </div>
      <div className="ml-5 shrink-0 rounded-md text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden">
        {button && (
          <button
            type="button"
            className="rounded bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-600 hover:bg-indigo-100"
            onClick={() => {
              button.onClick()
              sonnerToast.dismiss(id)
            }}
          >
            {button.label}
          </button>
        )}
      </div>
    </div>
  )
}

interface ToastProps {
  id: string | number
  title: string
  description: string
  button: {
    label: string
    onClick: () => void
  }
}

export { SonnerToaster, customToast, Toast as ToastPrimitives }
